import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

type Watch = {
  _id: string;
  name: string;
  imageUrl: string;
  price: number;
  slug: string;
  brand?: string;
};

export function SuggestedCarousell({ currentListingId }: { currentListingId: string }) {
  const [watches, setWatches] = useState<Watch[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLeftVisible, setIsLeftVisible] = useState(false);
  const [isRightVisible, setIsRightVisible] = useState(true);

  useEffect(() => {
    const fetchSuggestedWatches = async () => {
      try {
        const res = await fetch(`/api/suggest-carousell/suggested?currentListingId=${currentListingId}&limit=10`);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        setWatches(data.watches);
      } catch (error) {
        console.error('Error fetching suggested watches:', error);
      }
    };

    fetchSuggestedWatches();
  }, [currentListingId]);

  useEffect(() => {
    const checkScroll = () => {
      if (containerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
        setIsLeftVisible(scrollLeft > 0);
        setIsRightVisible(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      checkScroll();
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, [watches]);

  const scroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = direction === 'left' ? -800 : 800;
      containerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (watches.length === 0) {
    return null;
  }

  return (
    <div className="relative mt-16 px-4 md:px-0">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-slate-200">You May Also Like</h2>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll('left')}
            className={`transition-opacity ${!isLeftVisible ? 'opacity-0' : 'opacity-100'}`}
            disabled={!isLeftVisible}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll('right')}
            className={`transition-opacity ${!isRightVisible ? 'opacity-0' : 'opacity-100'}`}
            disabled={!isRightVisible}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory gap-4 pb-4"
        style={{ scrollBehavior: 'smooth' }}
      >
        {watches.map((watch) => (
          <Link href={`/details/${watch.slug}`} key={watch._id} className="snap-start">
            <Card className="flex-none w-72 bg-slate-800 border-slate-700 hover:border-slate-600 transition-all duration-300 group">
              <CardContent className="p-4">
                <div className="relative aspect-square w-full overflow-hidden rounded-lg mb-4">
                  <Image
                    src={watch.imageUrl}
                    alt={watch.name}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                    sizes="(max-width: 768px) 100vw, 288px"
                  />
                </div>
                <div className="space-y-2">
                  {watch.brand && (
                    <span className="text-sm font-medium text-slate-400">
                      {watch.brand}
                    </span>
                  )}
                  <h3 className="font-semibold text-slate-200 line-clamp-1">
                    {watch.name}
                  </h3>
                  <p className="text-lg font-bold text-slate-200">
                    ${watch.price.toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-slate-900 to-transparent pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-slate-900 to-transparent pointer-events-none" />
    </div>
  );
}
